// Valores compartilhados por todo o app: nao tem layout, so cor/dado. Nasceu
// da padronizacao da secao Marketing (plataformas, tons de status, paleta de
// grafico), mas nao ha nada especifico de Marketing aqui de proposito — quem
// precisar de "cor da plataforma X" ou "tom semantico Y" importa daqui.

export const BRAND = {
    600: '#7C3AED',
    700: '#6D28D9',
    DEFAULT: '#7C3AED',
    // = --primary no tema escuro (src/index.css) — #7C3AED sobre o fundo
    // escuro da so 3.36:1 de contraste; este valor mantem matiz/saturacao e
    // sobe a luminosidade ate passar no texto branco em cima tambem.
    dark: '#894DEF',
};

// Fonte unica de verdade de plataforma. Resolve o conflito de Instagram que
// existia entre analytics (#E1306C, em 2 dos 3 mapas locais) e Rastreamento
// (#E4405F): #E1306C vence. Google Ads usa o amarelo do produto (#FBBC04),
// nao o azul do "G", pra nao colidir com o azul do Facebook nos graficos.
export const PLATFORM = {
    instagram: { key: 'instagram', label: 'Instagram', icon: 'mdi:instagram', color: '#E1306C', tone: 'pink' },
    facebook: { key: 'facebook', label: 'Facebook', icon: 'mdi:facebook', color: '#1877F2', tone: 'info' },
    google_ads: { key: 'google_ads', label: 'Google Ads', icon: 'mdi:google-ads', color: '#FBBC04', tone: 'warning' },
    meta_ads: { key: 'meta_ads', label: 'Meta Ads', icon: 'mdi:facebook', color: '#0668E1', tone: 'brand' },
    whatsapp: { key: 'whatsapp', label: 'WhatsApp', icon: 'mdi:whatsapp', color: '#25D366', tone: 'success' },
    sms: { key: 'sms', label: 'SMS', icon: 'mdi:message-text-outline', color: '#3B82F6', tone: 'info' },
};

export const PLATFORM_ALIAS = {
    google: 'google_ads',
    instagram_ads: 'instagram',
    facebook_ads: 'facebook',
    meta: 'meta_ads',
    // provider das contas sociais conectadas pelo Embedded Signup — mesma
    // identidade visual do canal WhatsApp.
    whatsapp_business: 'whatsapp',
};

export function resolvePlatform(key) {
    if (!key) return null;
    const normalized = PLATFORM_ALIAS[key] ?? key;
    return PLATFORM[normalized] ?? null;
}

// Pares claro/escuro por tom semantico. Os valores `dark:` sao IDENTICOS ao
// que o shim legado de src/index.css (fora de @layer, depois de
// @tailwind utilities) ja pinta pra blue/green/red/amber/violet — escritos
// aqui pra documentar a intencao e sobreviver ao dia que o shim for removido.
// `pink` nao tem regra no shim: ali o `dark:` explicito e o que realmente vale.
export const TONE = {
    neutral: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-500/10 dark:text-gray-400 dark:border-gray-500/20',
    info: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/25',
    brand: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-500/10 dark:text-violet-400 dark:border-violet-500/25',
    success: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/25',
    warning: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/25',
    danger: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/25',
    pink: 'bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-500/10 dark:text-pink-400 dark:border-pink-500/25',
};

export const TONE_DOT = {
    neutral: 'bg-gray-400',
    info: 'bg-blue-500',
    brand: 'bg-violet-500',
    success: 'bg-green-500',
    warning: 'bg-amber-500',
    danger: 'bg-red-500',
    pink: 'bg-pink-500',
};

// Icon box "suave" (usado por MetricCard/emptyState/header): fundo tingido +
// cor do icone, com par dark.
export const TONE_SOFT = {
    neutral: { box: 'bg-gray-100 dark:bg-gray-500/10', icon: 'text-gray-500 dark:text-gray-400' },
    info: { box: 'bg-blue-50 dark:bg-blue-500/10', icon: 'text-blue-600 dark:text-blue-400' },
    brand: { box: 'bg-violet-100 dark:bg-violet-500/15', icon: 'text-violet-600 dark:text-violet-400' },
    success: { box: 'bg-green-50 dark:bg-green-500/10', icon: 'text-green-600 dark:text-green-400' },
    warning: { box: 'bg-amber-50 dark:bg-amber-500/10', icon: 'text-amber-600 dark:text-amber-400' },
    danger: { box: 'bg-red-50 dark:bg-red-500/10', icon: 'text-red-600 dark:text-red-400' },
    pink: { box: 'bg-pink-50 dark:bg-pink-500/10', icon: 'text-pink-600 dark:text-pink-400' },
};

export const CHART = {
    series: [
        { light: '#7C3AED', dark: '#A78BFA' },
        { light: '#3B82F6', dark: '#60A5FA' },
        { light: '#EC4899', dark: '#F472B6' },
        { light: '#F59E0B', dark: '#FBBF24' },
        { light: '#10B981', dark: '#34D399' },
        { light: '#6366F1', dark: '#818CF8' },
        { light: '#EF4444', dark: '#F87171' },
        { light: '#14B8A6', dark: '#2DD4BF' },
    ],
    axis: { tickLine: false, axisLine: false, tickMargin: 8, tick: { fontSize: 11 } },
    grid: { strokeDasharray: '3 3', vertical: false },
    barRadius: [4, 4, 0, 0],
    barRadiusH: [0, 4, 4, 0],
    // SVG `stroke`/`fill` aceitam `hsl(var(--x))` literalmente (recharts so
    // repassa a string pro atributo do elemento), entao os gráficos ficam
    // theme-aware sem precisar trocar de wrapper: `stroke="#f0f0f0"` era
    // invisível sobre o fundo escuro (~#1A1A1A); `hsl(var(--border))` resolve
    // pelo tema atual em tempo de pintura.
    gridStroke: 'hsl(var(--border))',
    tickFill: 'hsl(var(--muted-foreground))',
};

export function serieColor(index, dark = false) {
    const serie = CHART.series[index % CHART.series.length];
    return serie[dark ? 'dark' : 'light'];
}

// Spread direto num <Tooltip {...CHART_TOOLTIP_STYLE} .../> do recharts — os
// 3 props (contentStyle/labelStyle/itemStyle) sao aceitos no nivel raiz do
// componente. Sem isso o tooltip usa o fundo branco padrao do recharts, que
// destoa dos cards escuros no dark mode.
export const CHART_TOOLTIP_STYLE = {
    contentStyle: {
        backgroundColor: 'hsl(var(--popover))',
        border: '1px solid hsl(var(--border))',
        borderRadius: '0.75rem',
        fontSize: '12px',
        color: 'hsl(var(--popover-foreground))',
    },
    labelStyle: { color: 'hsl(var(--foreground))', fontWeight: 600, marginBottom: 4 },
    itemStyle: { color: 'hsl(var(--popover-foreground))' },
};
